// ============================================================================
//  Sidebar.tsx — NexusCorp ERP · Premium warm-dark sidebar
//  Dark Purple #1E1B3A · Violet #7C3AED · Raleway · Agency-grade design
// ============================================================================
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Calculator, Landmark, Users, UserCheck,
  FolderKanban, MessageSquare, FileText, ClipboardList, Calendar,
  GitBranch, Settings, TrendingUp, Ticket, Network, Receipt, Upload,
  ChevronDown, ChevronRight, BarChart2, Award, PanelLeftClose,
  PanelLeftOpen, X, ArrowLeftRight, Building2, Activity, Trophy,
  Mail, Truck, CheckSquare, Package, MapPin, Navigation, QrCode, Wallet, Tags,
  Fingerprint, Link2, Clock, Server, Share2,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { BrandMark } from './BrandMark'

/* ── Colors ────────────────────────────────────────────────── */
const C = {
  bg:       '#1E1B3A',
  bgHeader: '#181530',
  bgHover:  'rgba(255,255,255,0.05)',
  bgActive: 'rgba(124,58,237,0.22)',
  border:   'rgba(255,255,255,0.06)',
  text:     'rgba(255,255,255,0.55)',
  textH:    'rgba(255,255,255,0.92)',
  textM:    'rgba(255,255,255,0.34)',
  orange:   '#7C3AED',   // accent violet (nom conservé pour limiter le diff)
  orangeL:  '#C4B5FD',   // violet clair (item actif / label)
  amber:    '#8B5CF6',
  section:  'rgba(255,255,255,0.20)',
}

/* ── Navigation structure ──────────────────────────────────── */
interface NavItem {
  label: string; to?: string; icon: React.ElementType
  permission?: string; children?: NavItem[]
  end?: boolean // correspondance exacte (évite qu'un parent de route reste actif sur ses sous-routes)
}

const NAV: NavItem[] = [
  { label: 'Tableau de bord', to: '/dashboard', icon: LayoutDashboard },
  {
    label: 'Comptabilité', icon: Calculator, permission: 'accounting:view',
    children: [
      { label: 'Exercices', to: '/accounting/fiscal-years', icon: Calendar },
      { label: 'Journal', to: '/accounting/entries', icon: ClipboardList },
      { label: 'Grand livre', to: '/accounting/ledger', icon: FileText },
      { label: 'États financiers', to: '/accounting/reports/balance-sheet', icon: TrendingUp },
      { label: 'Plan comptable', to: '/accounting/accounts', icon: GitBranch },
    ],
  },
  {
    label: 'Trésorerie', icon: Landmark, permission: 'treasury:view',
    children: [
      { label: "Vue d'ensemble", to: '/treasury', icon: Landmark },
      { label: 'Comptes', to: '/treasury/accounts', icon: Landmark },
      { label: 'Transactions', to: '/treasury/transactions', icon: ArrowLeftRight },
      { label: 'Rapprochement', to: '/treasury/reconciliation', icon: ClipboardList },
      { label: 'Prévisionnel', to: '/treasury/forecast', icon: TrendingUp },
    ],
  },
  {
    label: 'Ressources Humaines', icon: Users, permission: 'hr:view',
    children: [
      { label: 'Employés', to: '/hr/employees', icon: Users },
      // Mêmes permissions que les gardes de routes : pas de lien qui renvoie au tableau de bord
      { label: 'Paie', to: '/hr/payroll', icon: Calculator, permission: 'payroll:view' },
      { label: 'Congés', to: '/hr/leaves', icon: ClipboardList, permission: 'leaves:view' },
      {
        label: 'Pointage', icon: Fingerprint,
        children: [
          { label: 'Tableau de bord', to: '/hr/biometric', icon: UserCheck, permission: 'attendance:view' },
          { label: 'Import badgeuse', to: '/hr/biometric/import', icon: Upload, permission: 'biometric:import' },
          { label: 'Associations badges', to: '/hr/biometric/associations', icon: Link2, permission: 'attendance:edit' },
          { label: 'Vue mensuelle', to: '/hr/biometric/monthly', icon: Calendar, permission: 'attendance:view' },
          { label: 'Terminaux', to: '/hr/biometric/devices', icon: Server, permission: 'attendance:view' },
          { label: 'Règles horaires', to: '/hr/attendance/rules', icon: Clock, permission: 'attendance:edit' },
        ],
      },
      { label: 'Évaluations', to: '/career/evaluations', icon: TrendingUp, permission: 'career:view' },
      { label: 'Formations', to: '/career/trainings', icon: Award, permission: 'career:view' },
    ],
  },
  { label: 'Projets', to: '/projects', icon: FolderKanban, permission: 'projects:view' },
  { label: 'Chat', to: '/chat', icon: MessageSquare, permission: 'chat:view' },
  {
    label: 'Documents', icon: FileText, permission: 'documents:view',
    children: [
      { label: 'Mes documents', to: '/documents', icon: FileText, end: true },
      { label: 'Documents partagés', to: '/documents/shared', icon: Share2 },
    ],
  },
  { label: 'Réunions', to: '/meetings', icon: ClipboardList, permission: 'meetings:view' },
  { label: 'Notes', to: '/notes', icon: FileText, permission: 'notes:view' },
  {
    label: 'CRM', icon: TrendingUp, permission: 'crm:view',
    children: [
      { label: 'Pipeline', to: '/crm', icon: TrendingUp },
      { label: 'Contacts', to: '/crm/contacts', icon: Users },
      { label: 'Entreprises', to: '/crm/companies', icon: Building2 },
      { label: 'Activités', to: '/crm/activities', icon: Activity },
      { label: 'Leaderboard', to: '/crm/leaderboard', icon: Trophy },
      { label: 'Objectifs', to: '/crm/targets', icon: BarChart2 },
      { label: 'Commissions', to: '/crm/commissions', icon: Award },
      { label: 'Plans comm.', to: '/crm/commission-plans', icon: Award },
      { label: 'Séquences', to: '/crm/sequences', icon: Mail },
      { label: 'Analytiques', to: '/crm/analytics', icon: BarChart2 },
      { label: 'Config pipeline', to: '/crm/pipeline/config', icon: Settings, permission: 'crm:configure' },
    ],
  },
  {
    label: 'Tickets', icon: Ticket, permission: 'tickets:view',
    children: [
      { label: 'Tableau de bord', to: '/tickets', icon: Ticket },
      { label: 'Tous les tickets', to: '/tickets/list', icon: ClipboardList },
      { label: 'Créer un ticket', to: '/tickets/create', icon: ClipboardList },
      { label: 'Catégories', to: '/tickets/categories', icon: Settings },
      { label: 'SLA', to: '/tickets/sla', icon: Settings },
    ],
  },
  { label: 'Organigramme', to: '/orgchart', icon: Network, permission: 'orgchart:view' },
  { label: 'Pointage Enrichi', icon: QrCode, to: '/attendance-enriched', permission: 'attendance_enriched:view' },
  {
    label: 'Facturation', icon: Receipt, permission: 'invoices:view',
    children: [
      { label: 'Tableau de bord', to: '/invoices', icon: Receipt },
      { label: 'Toutes les factures', to: '/invoices/list', icon: FileText },
      { label: 'Nouvelle facture', to: '/invoices/new', icon: Receipt },
      { label: 'Export FEC', to: '/invoices/export-fec', icon: FileText },
    ],
  },
  {
    label: 'Dépenses', icon: Wallet, permission: 'depenses:read',
    children: [
      { label: 'Toutes les dépenses', to: '/depenses', icon: Wallet },
      { label: 'Catégories', to: '/depenses/categories', icon: Tags, permission: 'depenses:manage_categories' },
    ],
  },
  { label: 'Imports', icon: Upload, permission: 'imports:view', children: [
    { label: 'Imports RH', to: '/imports?module=hr', icon: Users },
    { label: 'Imports Compta', to: '/imports?module=accounting', icon: Calculator },
  ]},
  {
    label: 'Administration', icon: Settings, permission: 'admin:view',
    children: [
      { label: 'Utilisateurs', to: '/admin/users', icon: Users },
      { label: 'Rôles', to: '/admin/roles', icon: Settings },
      { label: 'Permissions', to: '/admin/permissions', icon: Settings },
      { label: 'Entités', to: '/admin/entities', icon: Network },
      { label: 'Journaux', to: '/admin/audit', icon: ClipboardList },
      { label: 'Paramètres', to: '/admin/settings', icon: Settings },
    ],
  },
]

/* ── Logo ────────────────────────────────────────────────── */
function Logo({ mini }: { mini?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <BrandMark size={34} radius={10} />
      {!mini && (
        <div className="min-w-0">
          <p className="text-[14px] font-extrabold text-white tracking-[-0.02em] leading-none">NexusCorp</p>
          <p className="text-[8.5px] font-medium tracking-[0.14em] uppercase mt-[3px]" style={{ color: C.orangeL }}>Gestion intégrée</p>
        </div>
      )}
    </div>
  )
}

/* ── Section label ───────────────────────────────────────── */
function SectionLabel({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return <div className="h-px mx-2 my-2" style={{ background: C.border }} />
  return (
    <p className="px-3 pt-5 pb-1.5 text-[9.5px] font-semibold tracking-[0.12em] uppercase select-none" style={{ color: C.textM }}>
      {label}
    </p>
  )
}

/* ── Helpers arborescence (support 2 niveaux) ─────────────── */
// Filtre récursif par permission ; retire un (sous-)groupe devenu vide.
function filterNav(items: NavItem[], has: (p: string) => boolean): NavItem[] {
  const out: NavItem[] = []
  for (const it of items) {
    if (it.permission && !has(it.permission)) continue
    if (it.children) {
      const kids = filterNav(it.children, has)
      if (kids.length === 0) continue
      out.push({ ...it, children: kids })
    } else {
      out.push(it)
    }
  }
  return out
}
// Un item est actif si son `to` ou celui d'un descendant correspond à l'URL.
function matchPath(it: NavItem, pathname: string): boolean {
  if (it.to && (pathname === it.to || pathname.startsWith(it.to + '/'))) return true
  return (it.children ?? []).some(c => matchPath(c, pathname))
}
// Aplatit les feuilles (items avec `to`) — utilisé pour le flyout en mode réduit.
function leafItems(items: NavItem[]): NavItem[] {
  return items.flatMap(it => (it.children ? leafItems(it.children) : it.to ? [it] : []))
}

/* ── Sous-groupe dépliable (2e niveau) ────────────────────── */
function NavSubGroup({ item, pathname }: { item: NavItem; pathname: string }) {
  const active = (item.children ?? []).some(c => matchPath(c, pathname))
  const [open, setOpen] = useState(active)
  useEffect(() => { if (active) setOpen(true) }, [active])
  const Icon = item.icon
  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-[6px] rounded-lg text-[12.5px] border-0 cursor-pointer text-left transition-all duration-200"
        style={{ background: 'transparent', color: active ? C.orangeL : C.text }}>
        <Icon size={12} className="shrink-0" style={{ color: active ? C.orangeL : undefined }} />
        <span className="flex-1 truncate">{item.label}</span>
        <span className="opacity-40 shrink-0">{open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}</span>
      </button>
      {open && (
        <div className="ml-[13px] pl-3 mt-0.5 mb-0.5 space-y-[1px]" style={{ borderLeft: `1.5px solid rgba(124,58,237,0.12)` }}>
          {(item.children ?? []).map(sub => {
            const SI = sub.icon
            return (
              <NavLink key={sub.to} to={sub.to!} end
                className={({ isActive: ia }) =>
                  `flex items-center gap-2 px-2.5 py-[6px] rounded-lg text-[12px] no-underline transition-all duration-200 ${ia ? 'font-semibold' : ''}`
                }
                style={({ isActive: ia }) => ({
                  color: ia ? C.orangeL : C.text,
                  background: ia ? 'rgba(124,58,237,0.10)' : 'transparent',
                })}>
                {({ isActive: ia }) => (
                  <>
                    <SI size={11} className="shrink-0" style={{ color: ia ? C.orangeL : undefined }} />
                    <span className="truncate">{sub.label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Group nav (collapsible) ─────────────────────────────── */
function NavGroup({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation()
  const { hasPermission } = useAuthStore()
  const visibleChildren = filterNav(item.children ?? [], hasPermission)
  const isActive = visibleChildren.some(c => matchPath(c, location.pathname))
  const [open, setOpen] = useState(isActive)
  const Icon = item.icon

  useEffect(() => { if (isActive) setOpen(true) }, [isActive])

  if (collapsed) {
    return (
      <div className="relative group">
        <button className="w-full flex items-center justify-center p-2 rounded-[10px] transition-all duration-200 border-0 cursor-pointer"
          style={{ background: isActive ? C.bgActive : 'transparent', color: isActive ? C.orangeL : C.text }}
          title={item.label}
          onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = C.bgHover; e.currentTarget.style.color = C.textH } }}
          onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.text } }}>
          <Icon size={17} />
        </button>
        <div className="absolute left-[calc(100%+8px)] top-0 z-50 hidden group-hover:block">
          <div className="bg-white rounded-xl border border-[#E0E0E0] shadow-[0_12px_40px_rgba(0,0,0,0.15)] overflow-hidden min-w-[190px] py-1">
            <p className="px-4 pt-3 pb-2 text-[10px] font-bold tracking-[0.08em] uppercase text-[#9E9E9E] border-b border-[#EEEEEE] mb-1">{item.label}</p>
            {leafItems(visibleChildren).map(child => {
              const CI = child.icon
              const ca = child.to ? (child.end ? location.pathname === child.to : location.pathname.startsWith(child.to)) : false
              return (
                <NavLink key={child.to} to={child.to!}
                  className={`flex items-center gap-2.5 px-4 py-[8px] text-[13px] no-underline transition-colors ${ca ? 'text-[#6D28D9] bg-[#F3EEFF] font-semibold' : 'text-[#424242] hover:bg-[#FAFAFA]'}`}>
                  <CI size={14} className="shrink-0 opacity-70" />{child.label}
                </NavLink>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2.5 px-2.5 py-[8px] rounded-[10px] border-0 cursor-pointer text-left transition-all duration-200 text-[13px]"
        style={{
          background: isActive ? C.bgActive : 'transparent',
          color: isActive ? C.orangeL : C.text,
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = C.bgHover; e.currentTarget.style.color = C.textH } }}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.text } }}>
        <Icon size={15} className="shrink-0" />
        <span className="flex-1 truncate font-medium">{item.label}</span>
        <span className="opacity-40 shrink-0">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
      </button>
      {open && (
        <div className="ml-[19px] pl-3 mt-1 mb-1 space-y-[1px]" style={{ borderLeft: `1.5px solid rgba(124,58,237,0.15)` }}>
          {visibleChildren.map(child => {
            if (child.children) return <NavSubGroup key={child.label} item={child} pathname={location.pathname} />
            const CI = child.icon
            return (
              <NavLink key={child.to} to={child.to!} end={child.end}
                className={({ isActive: ia }) =>
                  `flex items-center gap-2 px-2.5 py-[6px] rounded-lg text-[12.5px] no-underline transition-all duration-200 ${ia ? 'font-semibold' : ''}`
                }
                style={({ isActive: ia }) => ({
                  color: ia ? C.orangeL : C.text,
                  background: ia ? 'rgba(124,58,237,0.10)' : 'transparent',
                })}>
                {({ isActive: ia }) => (
                  <>
                    <CI size={12} className="shrink-0" style={{ color: ia ? C.orangeL : undefined }} />
                    <span className="truncate">{child.label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Single nav item ─────────────────────────────────────── */
function NavSingle({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon
  return (
    <NavLink to={item.to!} end={item.to === '/dashboard'} title={item.label}
      className={({ isActive }) =>
        `flex items-center ${collapsed ? 'justify-center p-2' : 'gap-2.5 px-2.5 py-[8px]'} rounded-[10px] no-underline transition-all duration-200 text-[13px] font-medium`
      }
      style={({ isActive }) => ({
        background: isActive ? C.bgActive : 'transparent',
        color: isActive ? C.orangeL : C.text,
      })}>
      {({ isActive }) => (
        <>
          <Icon size={collapsed ? 17 : 15} className="shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {isActive && !collapsed && (
            <div className="ml-auto w-[5px] h-[5px] rounded-full shrink-0" style={{ background: C.orange, boxShadow: `0 0 8px ${C.orange}` }} />
          )}
        </>
      )}
    </NavLink>
  )
}

/* ── Sidebar inner ───────────────────────────────────────── */
function SidebarInner({ collapsed, mobile = false }: { collapsed: boolean; mobile?: boolean }) {
  const { user, hasPermission } = useAuthStore()
  const { toggleSidebarCollapsed, setSidebarOpen } = useUIStore()
  const c = collapsed && !mobile
  const vis = (NAV || []).filter(i => !i.permission || hasPermission(i.permission))

  const core = vis.filter(i => ['Tableau de bord', 'Comptabilité', 'Trésorerie', 'Ressources Humaines'].includes(i.label))
  const work = vis.filter(i => ['Projets', 'Chat', 'Documents', 'Réunions', 'Notes'].includes(i.label))
  const biz = vis.filter(i => ['CRM', 'Tickets', 'Organigramme', 'Facturation', 'Dépenses', 'Imports', 'Pointage Enrichi'].includes(i.label))
  const admin = vis.filter(i => ['Administration'].includes(i.label))

  const render = (items: NavItem[]) =>
    items.map(item =>
      item.children
        ? <NavGroup key={item.label} item={item} collapsed={c} />
        : <NavSingle key={item.label} item={item} collapsed={c} />,
    )

  return (
    <div className="flex flex-col h-full relative overflow-hidden" style={{ background: C.bg }}>
      {/* Ambient glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none z-0"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 65%)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-0"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.15) 100%)' }} />

      {/* Header */}
      <div className={`relative z-10 flex items-center h-[60px] shrink-0 ${c ? 'px-3 justify-center' : 'px-4 justify-between'}`}
        style={{ borderBottom: `1px solid ${C.border}` }}>
        {c ? <Logo mini /> : <Logo />}
        {mobile ? (
          <button onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-lg border-0 cursor-pointer transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', color: C.text }}>
            <X size={15} />
          </button>
        ) : (
          <button onClick={toggleSidebarCollapsed}
            className="p-1 rounded-lg border-0 bg-transparent cursor-pointer transition-colors shrink-0"
            style={{ color: C.textM }}
            onMouseEnter={e => { e.currentTarget.style.color = C.textH }}
            onMouseLeave={e => { e.currentTarget.style.color = C.textM }}>
            {c ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className={`relative z-10 flex-1 overflow-y-auto overflow-x-hidden ${c ? 'px-1.5 py-3' : 'px-2.5 py-3'} space-y-[2px]`}>
        {render(core)}
        {work.length > 0 && <><SectionLabel label="Collaboration" collapsed={c} />{render(work)}</>}
        {biz.length > 0 && <><SectionLabel label="Business" collapsed={c} />{render(biz)}</>}
        {admin.length > 0 && <><SectionLabel label="Admin" collapsed={c} />{render(admin)}</>}
      </nav>

      {/* Profile */}
      <div className="relative z-10 p-2.5 shrink-0" style={{ borderTop: `1px solid ${C.border}` }}>
        <NavLink to="/profile" onClick={() => mobile && setSidebarOpen(false)}
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl no-underline transition-all duration-200"
          style={({ isActive }) => ({ background: isActive ? C.bgActive : 'rgba(255,255,255,0.03)' })}
          onMouseEnter={e => { e.currentTarget.style.background = C.bgHover }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}>
          {c ? (
            <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.amber})` }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          ) : (
            <>
              <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.amber})`, boxShadow: '0 2px 8px rgba(124,58,237,0.25)' }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold leading-tight truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-[10px] truncate" style={{ color: C.textM }}>{user?.email}</p>
              </div>
            </>
          )}
        </NavLink>
      </div>
    </div>
  )
}

/* ── Export ───────────────────────────────────────────────── */
export default function Sidebar() {
  const { sidebarCollapsed, sidebarOpen } = useUIStore()
  return (
    <>
      <aside className="hidden lg:flex flex-col h-screen sticky top-0 overflow-hidden transition-all duration-300"
        style={{ width: sidebarCollapsed ? 60 : 256, minWidth: sidebarCollapsed ? 60 : 256 }}>
        <SidebarInner collapsed={sidebarCollapsed} />
      </aside>
      <aside className="lg:hidden fixed inset-y-0 left-0 z-30 w-[256px] transition-transform duration-300"
        style={{ transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)' }}>
        <SidebarInner collapsed={false} mobile />
      </aside>
    </>
  )
}
